import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid2 as Grid,
  Typography,
  CircularProgress,
  Tooltip,
  Container,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { formatUTCToKST } from "@/utils";
import type { BossBabyChatSessionDto } from "@/apis/bossBabyChatLog";

/**
 * SessionList 템플릿 Props
 */
export type SessionListProps = {
  sessions: BossBabyChatSessionDto[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  filters: {
    search: string;
    comId: string;
    status: string;
    startDate: string;
    endDate: string;
  };
  snackbarOpen: boolean;
  snackbarMessage: string;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onLimitChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onFilterChange: (key: string, value: string) => void;
  onResetFilters: () => void;
  onRefresh: () => void;
  onViewSession: (session: BossBabyChatSessionDto) => void;
  onSnackbarClose: () => void;
};

/**
 * 보스베이비 채팅 세션 리스트 템플릿
 *
 * @description 보스베이비 채팅 세션 목록을 표시하고 관리하는 템플릿 컴포넌트
 * @param props SessionListProps
 * @returns JSX.Element
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-29
 */
export const SessionList = (props: SessionListProps) => {
  const {
    sessions,
    loading,
    error,
    pagination,
    filters,
    onPageChange,
    onLimitChange,
    onFilterChange,
    onResetFilters,
    onRefresh,
    onViewSession,
  } = props;

  const [localFilters, setLocalFilters] = useState(filters);

  // 필터 변경 핸들러
  const handleFilterInputChange = (key: string, value: string) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  };

  // 필터 적용
  const handleApplyFilters = () => {
    Object.entries(localFilters).forEach(([key, value]) => {
      onFilterChange(key, value);
    });
  };

  // 필터 초기화
  const handleClearFilters = () => {
    const emptyFilters = {
      search: "",
      comId: "",
      status: "",
      startDate: "",
      endDate: "",
    };
    setLocalFilters(emptyFilters);
    onResetFilters();
  };

  // 세션 상태 색상
  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "completed":
        return "default";
      case "expired":
        return "error";
      default:
        return "default";
    }
  };

  // 세션 상태 텍스트
  const getStatusText = (status: string) => {
    switch (status) {
      case "active":
        return "활성";
      case "completed":
        return "완료";
      case "expired":
        return "만료";
      default:
        return status;
    }
  };

  return (
    <Container maxWidth="xl">
      {/* 필터 섹션 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="검색"
              placeholder="사용자 ID, 세션 UUID"
              value={localFilters.search}
              onChange={(e) =>
                handleFilterInputChange("search", e.target.value)
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="회사 ID"
              placeholder="회사 ID"
              value={localFilters.comId}
              onChange={(e) => handleFilterInputChange("comId", e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>상태</InputLabel>
              <Select
                value={localFilters.status}
                label="상태"
                onChange={(e) =>
                  handleFilterInputChange("status", e.target.value)
                }
              >
                <MenuItem value="">전체</MenuItem>
                <MenuItem value="active">활성</MenuItem>
                <MenuItem value="completed">완료</MenuItem>
                <MenuItem value="expired">만료</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="시작 날짜"
              value={localFilters.startDate}
              onChange={(e) =>
                handleFilterInputChange("startDate", e.target.value)
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="종료 날짜"
              value={localFilters.endDate}
              onChange={(e) =>
                handleFilterInputChange("endDate", e.target.value)
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 1 }}>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyFilters}
              >
                적용
              </Button>
              <Tooltip title="필터 초기화">
                <IconButton size="small" onClick={handleClearFilters}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="새로고침">
                <IconButton size="small" onClick={onRefresh}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 테이블 섹션 */}
      <Paper>
        <TableContainer>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box p={4}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>세션 UUID</TableCell>
                    <TableCell>회사명</TableCell>
                    <TableCell>사용자 ID</TableCell>
                    <TableCell>사용자 이름</TableCell>
                    <TableCell align="center">상태</TableCell>
                    <TableCell align="center">메시지 수</TableCell>
                    <TableCell>마지막 활동</TableCell>
                    <TableCell>생성일시</TableCell>
                    <TableCell align="center">액션</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography color="text.secondary">
                          조회된 세션이 없습니다.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sessions.map((session) => (
                      <TableRow key={session.sessionId} hover>
                        <TableCell>
                          {session.sessionUuid.substring(0, 8)}...
                        </TableCell>
                        <TableCell>{session.companyName || "-"}</TableCell>
                        <TableCell>{session.userId}</TableCell>
                        <TableCell>{session.userName || "-"}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={getStatusText(session.status)}
                            color={
                              getStatusColor(session.status) as
                                | "success"
                                | "default"
                                | "error"
                                | "warning"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {session.messageCount}
                        </TableCell>
                        <TableCell>
                          {formatUTCToKST(session.lastActivity)}
                        </TableCell>
                        <TableCell>{formatUTCToKST(session.regDttm)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="상세 보기">
                            <IconButton
                              size="small"
                              onClick={() => onViewSession(session)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={onPageChange}
                rowsPerPage={pagination.limit}
                onRowsPerPageChange={onLimitChange}
                rowsPerPageOptions={[10, 20, 50, 100]}
              />
            </>
          )}
        </TableContainer>
      </Paper>
    </Container>
  );
};
